import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';

export interface SeoPageConfig {
  titleKey: string;
  descriptionKey?: string;
  titleParams?: Record<string, string | number>;
  descriptionParams?: Record<string, string | number>;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  readonly #meta = inject(Meta);
  readonly #title = inject(Title);
  readonly #translate = inject(TranslateService);

  initMeta(): void {
    this.#updateMetaTag('name', 'apple-mobile-web-app-title', environment.projectName);
    this.#updateMetaTag('property', 'og:site_name', environment.projectName);
  }

  setPage(config: SeoPageConfig): void {
    const pageTitle = this.#translate.instant(config.titleKey, config.titleParams);
    const pageDescription = config.descriptionKey
      ? this.#translate.instant(config.descriptionKey, config.descriptionParams)
      : '';
    const documentTitle = pageTitle
      ? `${pageTitle} | ${environment.projectName}`
      : environment.projectName;

    this.#title.setTitle(documentTitle);
    this.#updateMetaTag('name', 'description', pageDescription || null);
    this.#updateMetaTag('property', 'og:title', documentTitle);
    this.#updateMetaTag('property', 'og:description', pageDescription || null);
    this.#updateMetaTag('name', 'twitter:title', documentTitle);
    this.#updateMetaTag('name', 'twitter:description', pageDescription || null);
  }

  #updateMetaTag(
    attributeName: 'name' | 'property',
    attributeValue: string,
    content: string | null,
  ): void {
    const selector = `${attributeName}="${attributeValue}"`;

    if (!content) {
      this.#meta.removeTag(selector);
      return;
    }

    this.#meta.updateTag({
      [attributeName]: attributeValue,
      content,
    });
  }
}
